// User types for Mission Control Health Coaching Platform
// Based on reference codebase types with Mission Control-specific modifications

export interface User {
  id: string;
  first_name: string;  // Mission Control uses snake_case
  last_name: string;
  email: string;
  phone?: string;
  role?: string;  // Legacy field - kept for backward compatibility
  user_type?: 'client' | 'staff';  // Account type: client vs staff
  roles?: string[];  // Staff permission roles: ['admin'], ['coach'], ['admin', 'coach']
  status?: string;
  createdAt?: string;
  updatedAt?: string;

  // Extended profile fields for health coaching
  dateOfBirth?: string;
  gender?: string;
  weight?: number;        // in pounds - current weight
  startingWeight?: number; // in pounds - baseline weight (set once, immutable for clients)
  goalWeight?: number;     // in pounds - target weight for journey completion
  heightFeet?: number;    // Mission Control uses separate feet/inches fields
  heightInches?: number;
  timezone?: string;

  // Goal management permissions
  client_phase?: number;     // 0-3, determines which goal templates are available
  self_managed?: boolean;    // If true, client can create/edit/delete own goals
  
  // Full address components
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  
  // Optional profile fields
  occupation?: string;
  referredBy?: string;
  additionalNotes?: string;
  menstrualCycle?: string;
  
  // Profile completion tracking
  profileComplete?: boolean;
}

export interface CreateUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  sendWelcomeEmail: boolean;
  customMessage?: string;
}

export interface CreateUserResponse {
  success: boolean;
  user?: User;
  authToken?: string;
  message: string;
  emailSent?: boolean;
}

export interface OnboardingVerifyEmailRequest {
  email: string;
}

export interface OnboardingVerifyEmailResponse {
  success: boolean;
  message: string;
  userExists?: boolean;
  tokenExpired?: boolean;
}

export interface OnboardingVerifyTokenRequest {
  email: string;
  token: string;
}

export interface OnboardingVerifyTokenResponse {
  success: boolean;
  message: string;
  user?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface OnboardingCompleteRequest {
  email: string;
  token: string;
  firstName: string;
  lastName: string;
  password: string;
}

export interface OnboardingCompleteResponse {
  success: boolean;
  message: string;
  user?: User;
}

export interface UserProfile {
  // Required profile fields for Mission Control
  dateOfBirth: string;
  gender: string;
  weight: number;
  heightFeet: number;
  heightInches: number;
  timezone: string;
  
  // Full address (required)
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  
  // Optional fields
  occupation?: string;
  referredBy?: string;
  additionalNotes?: string;
  menstrualCycle?: string;
}

export interface UpdateProfileRequest extends Partial<UserProfile> {
  // Allow partial updates to profile
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export interface UpdateProfileResponse {
  success: boolean;
  message: string;
  user?: User;
}

export interface GetProfileResponse {
  success: boolean;
  message: string;
  profile?: User;
  isProfileComplete?: boolean;
  missingFields?: string[];
}

// Profile completion validation helper
export function isProfileComplete(user: User): boolean {
  const requiredFields = [
    'first_name',
    'last_name', 
    'email',
    'phone',
    'dateOfBirth',
    'gender',
    'weight',
    'heightFeet', 
    'heightInches',
    'timezone',
    'addressLine1',
    'city',
    'state',
    'zipCode',
    'country'
  ];

  return requiredFields.every(field => {
    const value = user[field as keyof User];
    return value !== undefined && value !== null && value !== '';
  });
}

// Get missing required fields
export function getMissingFields(user: User): string[] {
  const requiredFields = [
    'first_name',
    'last_name', 
    'email',
    'phone',
    'dateOfBirth',
    'gender',
    'weight',
    'heightFeet', 
    'heightInches',
    'timezone',
    'addressLine1',
    'city',
    'state',
    'zipCode',
    'country'
  ];

  return requiredFields.filter(field => {
    const value = user[field as keyof User];
    return value === undefined || value === null || value === '';
  });
}

// Field display names for user-friendly error messages
export const FIELD_DISPLAY_NAMES: Record<string, string> = {
  first_name: 'First Name',
  last_name: 'Last Name',
  email: 'Email',
  phone: 'Phone',
  dateOfBirth: 'Date of Birth',
  gender: 'Gender',
  weight: 'Weight',
  heightFeet: 'Height (Feet)',
  heightInches: 'Height (Inches)',
  timezone: 'Timezone',
  addressLine1: 'Address Line 1',
  city: 'City',
  state: 'State',
  zipCode: 'ZIP Code',
  country: 'Country'
};